// MIDAS V3 — Resizable pane divider between chat and artifact dock (Phase 3)
//
// Visually a 1px hairline at rest with an 8px-wide invisible hit zone.
// States:
//   • Rest: hairline var(--border)
//   • Hover: hairline tints to var(--accent) + 5-dot vertical grip appears
//   • Drag: 2px accent stripe + soft accent band bg + floating mono chip
//           below cursor shows live `{chatW} / {dockW} px`
//
// Constraints (also enforced upstream in MidasV3Layout):
//   • MIN_CHAT_WIDTH = 480
//   • MIN_DOCK_WIDTH = 360
//   • DEFAULT_DOCK_WIDTH = 620
//
// The divider does not own the drag math — it just emits onDrag(clientX) when
// the user is actively dragging. The layout component clamps + persists.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useEffect, useRef, useState } from "react";

export const MIN_CHAT_WIDTH = 480;
export const MIN_DOCK_WIDTH = 360;
export const DEFAULT_DOCK_WIDTH = 620;

export interface MidasV3PaneDividerV3Props {
  /** Called on every mousemove while the user is dragging. */
  onDrag: (clientX: number) => void;
  /** Live chat-column width in px (for the readout chip during drag). */
  chatWidth: number;
  /** Live dock-column width in px (for the readout chip during drag). */
  dockWidth: number;
  /** Called when drag starts (so layout can capture initial widths if needed). */
  onDragStart?: () => void;
  /** Called when drag ends (layout uses this to persist the final width). */
  onDragEnd?: () => void;
}

const MidasV3PaneDividerV3: React.FC<MidasV3PaneDividerV3Props> = ({
  onDrag,
  chatWidth,
  dockWidth,
  onDragStart,
  onDragEnd,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Live cursor coords so the readout chip follows the mouse without re-layout
  // thrashing. We re-render the chip on each move (cheap — single fixed div).
  const [cursor, setCursor] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Throttle persist on drag end (parent persists the final value to Redux).
  const lastMoveRef = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      setCursor({ x: e.clientX, y: e.clientY });
      setIsDragging(true);
      onDragStart?.();
    },
    [onDragStart]
  );

  // Window listeners while dragging.
  useEffect(() => {
    if (!isDragging) return;
    const move = (e: MouseEvent) => {
      // Always update the cursor for the chip; throttle parent dispatch.
      setCursor({ x: e.clientX, y: e.clientY });
      const now = performance.now();
      if (now - lastMoveRef.current >= 16) {
        lastMoveRef.current = now;
        onDrag(e.clientX);
      } else {
        // Still fire the final position so the dock width matches cursor.
        onDrag(e.clientX);
      }
    };
    const up = () => {
      setIsDragging(false);
      onDragEnd?.();
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [isDragging, onDrag, onDragEnd]);

  const isHot = isHovering || isDragging;

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize artifact dock"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseDown={handleMouseDown}
      style={{
        position: "relative",
        width: 8,
        flexShrink: 0,
        height: "100%",
        cursor: "col-resize",
        zIndex: 5,
        background: isDragging ? "var(--accent-soft)" : "transparent",
        transition: "background 0.12s ease",
        userSelect: "none",
      }}
    >
      {/* Hairline / accent line at the center of the hit zone */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          width: isDragging ? 2 : 1,
          marginLeft: isDragging ? -1 : -0.5,
          background: isHot ? "var(--accent)" : "var(--border)",
          transition: "background 0.12s ease, width 0.12s ease",
          pointerEvents: "none",
        }}
      />
      {/* 5-dot vertical grip — revealed on hover/drag */}
      {isHot && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            pointerEvents: "none",
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                width: 3,
                height: 3,
                borderRadius: 999,
                background: "var(--accent)",
              }}
            />
          ))}
        </div>
      )}
      {/* Floating width readout chip during drag — `position: fixed` follows
          the cursor across any pane and isn't clipped by overflow:hidden. */}
      {isDragging && (
        <div
          style={{
            position: "fixed",
            left: cursor.x,
            top: cursor.y + 18,
            transform: "translateX(-50%)",
            pointerEvents: "none",
            zIndex: 9999,
            padding: "4px 9px",
            borderRadius: 4,
            background: "var(--ink)",
            color: "var(--bg)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
            letterSpacing: "0.02em",
          }}
        >
          {Math.round(chatWidth)} / {Math.round(dockWidth)} px
        </div>
      )}
    </div>
  );
};

export default MidasV3PaneDividerV3;
